import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import './LeagueLandingAvailabilityModal.css'

const DISMISSIBLE_VARIANTS = ['soft', 'partner-soft']

/**
 * LeagueLandingAvailabilityModal
 *
 * Modal shown on league landing page to enforce availability requirement (Epic #1699).
 * Displays either a "hard" (blocking) or "soft" (dismissible) modal based on user/opponent availability state.
 *
 * Modal Logic:
 * - Hard modal: User never entered availability OR dead league (no future availability for anyone)
 *   - User must "Enter Availability" or "Go Back Home"
 *   - Non-dismissible (no X button, no backdrop dismiss, focus trap)
 * - Soft modal: User has no future availability, but opponents do
 *   - User can "Enter Availability" or "Maybe Later" (dismiss)
 *   - Dismissible (X button, backdrop click, escape key)
 * - Partner-soft modal: User availability is set, but their doubles partner still needs theirs
 *   - User can acknowledge the reminder and continue browsing
 *   - Dismissible (X button, backdrop click, escape key)
 *
 * Accessibility:
 * - Hard modal implements focus trap to prevent tabbing outside
 * - Soft modal allows escape key and backdrop click to dismiss
 * - ARIA labels for screen readers
 *
 * @component
 */
export default function LeagueLandingAvailabilityModal({
  show,
  variant,
  leagueId: _leagueId, // Reserved for future use (navigation/tracking)
  onDismiss,
  onNavigateHome,
  onEnterAvailability,
}) {
  const isHard = variant === 'hard'
  const isSoft = variant === 'soft'
  const isPartnerSoft = variant === 'partner-soft'
  const isDismissibleVariant = DISMISSIBLE_VARIANTS.includes(variant)

  // Handle escape key (dismissible variants only)
  useEffect(() => {
    if (!show || !isDismissibleVariant) return

    const handleEscape = (e) => {
      if (e.key === 'Escape' && onDismiss) {
        e.preventDefault()
        onDismiss()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [show, isDismissibleVariant, onDismiss])

  // Handle focus trap (hard modal only)
  useEffect(() => {
    if (!show || variant !== 'hard') return

    const dialog = document.querySelector('.ll-availability-modal')
    const focusableElements = dialog?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (!focusableElements?.length) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTab = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    // Set initial focus
    firstElement?.focus()

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [show, variant])

  const handleBackdropClick = (e) => {
    // Only allow backdrop dismiss for dismissible variants
    if (isDismissibleVariant && e.target === e.currentTarget && onDismiss) {
      onDismiss()
    }
  }

  const handleCloseClick = () => {
    if (isDismissibleVariant && onDismiss) {
      onDismiss()
    }
  }

  if (!show) return null

  return (
    <div
      className="fl-sheet-overlay"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ll-availability-modal-title"
      onClick={handleBackdropClick}
      data-testid="availability-modal-overlay"
    >
      <div
        className={`fl-sheet ll-availability-modal ll-availability-modal--${variant}`}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fl-sheet-header ll-availability-modal-header">
          <h2 id="ll-availability-modal-title" className="fl-sheet-title">
            {isHard && '🗓️ Availability Required to Continue'}
            {isSoft && '🗓️ Update Your Availability'}
            {isPartnerSoft && '🤝 Nudge Your Partner to Add Availability'}
          </h2>
          {isDismissibleVariant && (
            <button
              className="ll-availability-modal-close"
              onClick={handleCloseClick}
              aria-label="Close availability reminder"
              data-testid="close-button"
            >
              ✕
            </button>
          )}
        </div>

        <div className="fl-sheet-body ll-availability-modal-body">
          {isHard && (
            <>
              <p className="ll-availability-modal-text">
                You need to add your availability before accessing this league.
              </p>
              <p className="ll-availability-modal-text">
                This ensures you and your opponents can find times to play.
              </p>
            </>
          )}
          {isSoft && (
            <>
              <p className="ll-availability-modal-text">
                You don't have any upcoming availability, but some of your
                opponents do.
              </p>
              <p className="ll-availability-modal-text">
                Add your availability to increase your chances of scheduling
                matches.
              </p>
            </>
          )}
          {isPartnerSoft && (
            <>
              <p className="ll-availability-modal-text">
                Your availability is set, but your doubles partner still needs
                to add theirs.
              </p>
              <p className="ll-availability-modal-text">
                You can keep browsing the league. Encourage your partner to add
                availability so you can schedule faster.
              </p>
            </>
          )}
        </div>

        <div className="fl-actions ll-availability-modal-footer">
          {isHard && (
            <>
              <button
                type="button"
                className="fl-btn fl-btn-secondary ll-availability-btn-secondary"
                onClick={onNavigateHome}
                disabled={!onNavigateHome}
                data-testid="go-home-button"
              >
                Go Back Home
              </button>
              <button
                type="button"
                className="fl-btn fl-btn-primary ll-availability-btn-primary"
                onClick={onEnterAvailability}
                disabled={!onEnterAvailability}
                data-testid="enter-availability-button"
              >
                Enter Availability
              </button>
            </>
          )}
          {isSoft && (
            <>
              <button
                type="button"
                className="fl-btn fl-btn-secondary ll-availability-btn-secondary"
                onClick={onDismiss}
                disabled={!onDismiss}
                data-testid="maybe-later-button"
              >
                Maybe Later
              </button>
              <button
                type="button"
                className="fl-btn fl-btn-primary ll-availability-btn-primary"
                onClick={onEnterAvailability}
                disabled={!onEnterAvailability}
                data-testid="enter-availability-button"
              >
                Enter Availability
              </button>
            </>
          )}
          {isPartnerSoft && (
            <button
              type="button"
              className="fl-btn fl-btn-primary ll-availability-btn-primary"
              onClick={onDismiss}
              disabled={!onDismiss}
              data-testid="continue-browsing-button"
            >
              Continue Browsing
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Custom PropTypes validator for variant-specific required callbacks
const variantCallbackValidator = (callbackName, requiredForVariants) => {
  return (props, propName, componentName) => {
    const { variant, [propName]: callback } = props
    const requiredVariants = Array.isArray(requiredForVariants)
      ? requiredForVariants
      : [requiredForVariants]
    if (requiredVariants.includes(variant) && !callback) {
      return new Error(
        `The prop \`${propName}\` is required when \`variant\` is one of [${requiredVariants.join(', ')}] in \`${componentName}\`.`,
      )
    }
    if (callback && typeof callback !== 'function') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof callback}\` supplied to \`${componentName}\`, expected \`function\`.`,
      )
    }
    return null
  }
}

LeagueLandingAvailabilityModal.propTypes = {
  /** Whether to show the modal */
  show: PropTypes.bool.isRequired,

  /** Modal variant: 'hard' (blocking), 'soft' (user missing), or 'partner-soft' (partner missing) */
  variant: PropTypes.oneOf(['hard', 'soft', 'partner-soft']).isRequired,

  /** League ID (reserved for future navigation/tracking features) */
  leagueId: PropTypes.number.isRequired,

  /** Callback when user dismisses soft modal (required for soft variant, ignored for hard) */
  onDismiss: variantCallbackValidator('onDismiss', DISMISSIBLE_VARIANTS),

  /** Callback when user clicks "Go Back Home" (required for hard variant, ignored for soft) */
  onNavigateHome: variantCallbackValidator('onNavigateHome', 'hard'),

  /** Callback when user clicks "Enter Availability" */
  onEnterAvailability: PropTypes.func.isRequired,
}
